import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../lib/utils';

export interface CopyFieldProps extends React.ComponentPropsWithoutRef<'div'> {
  /** The exact string copied to the clipboard — nothing is appended. */
  value: string;
  /** Optional uppercase field label rendered above the value. */
  label?: string;
  /** Rendered (non-copyable) when `value` is empty. Defaults to an em dash. */
  placeholder?: string;
}

/**
 * A compact, labeled single field with an inline copy button that copies its
 * exact `value`. Pair with `CopyBlock` (which keeps the clamp/expand behavior
 * for long values) when laying out structured records — e.g. DNS records where
 * each of Type / Host / Priority must be copied atomically into a provider form.
 */
export function CopyField({
  value,
  label,
  placeholder = '—',
  className,
  ...props
}: CopyFieldProps) {
  const [copied, setCopied] = React.useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }

  const hasValue = value.length > 0;

  return (
    <div className={cn('space-y-1.5', className)} {...props}>
      {label ? (
        <p className="text-xs font-medium text-muted-foreground">
          {label}
        </p>
      ) : null}
      <div className="flex items-center gap-2 rounded-md border border-border bg-subtle px-3 py-2">
        <span
          className={cn(
            'min-w-0 flex-1 truncate font-mono text-xs',
            hasValue ? 'text-foreground' : 'text-muted-foreground'
          )}
          title={hasValue ? value : undefined}
        >
          {hasValue ? value : placeholder}
        </span>
        {hasValue ? (
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={label ? `Copy ${label}` : 'Copy to clipboard'}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-success" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}
